import { EditMembers, EditTeam, Settings } from './containers';
import {
  IAppComponent,
  IAppComponentDispatchToProps,
  IAppComponentMapStateToProps,
} from './App.d';
import React, { Component } from 'react';
import { Switch } from 'react-router-dom';
import { Trans, withTranslation } from 'react-i18next';
import { apiThunk, appActions } from './actions';
import { deleteCookie, getMatchedRouteParams } from './utils';
import {
  getRequestIsError,
  getRequestIsInFlight,
} from './selectors/loadingSelectors';
import { getTeamCharityId, getTeamGuid } from './selectors/apiSelectors';

import AdminTabs from './components/AdminTabs/AdminTabs';
import Alert from '@justgiving/alert';
import ErrorBoundary from '@justgiving/error-boundary';
import ErrorPage from '@justgiving/error-page';
import { IAppState } from './types/state.d';
import Loading from '@justgiving/loading';
import { PageOwnerRoute } from './components/PageOwnerRoute/PageOwnerRoute';
import { PageType } from '../types/graphql';
import { ROUTES } from './consts';
import { TrackingProvider } from '@justgiving/react-tracking';
import analytics from './utils/analytics';
import { captureException } from '@sentry/react';
import config from './config';
import { connect } from 'react-redux';
import { getPageId } from './selectors/appSelectors';
import queryString from 'query-string';
import { withRouter } from 'react-router';

type AppState = {
  noTeamDataRequired: boolean;
  noPageId: boolean;
};

export class App extends Component<IAppComponent, AppState> {
  state = {
    noTeamDataRequired: false,
    noPageId: false,
  };

  componentDidMount() {
    const {
      history: {
        location: { pathname, search },
      },
      savePageId,
      fetchAppData,
      savePageType,
    } = this.props;

    // keeping frpid for backwards compatibility
    const { frpid, pageId } = queryString.parse(search);
    const { teamGuid } = getMatchedRouteParams(pathname);

    if (pageId || frpid) {
      savePageId((pageId as string) || (frpid as string));
      this.setState({
        noTeamDataRequired: true,
      });
    } else if (pathname === ROUTES.INDEX) {
      this.setState({
        noPageId: true,
      });
    }

    savePageType(pageId ? PageType.OnePage : PageType.Fundraising);

    if (teamGuid) {
      fetchAppData(teamGuid);
    }

    deleteCookie(document, config.activeGroupCookieName);
  }

  onComponentDidCatch = (error: Error) => {
    captureException(error);
  };

  render() {
    const {
      isFatalError,
      isInFlight,
      t,
      pageId,
      teamGuid,
      charityId,
      errorStatus,
    } = this.props;

    const { noTeamDataRequired, noPageId } = this.state;
    const displayLoading =
      !noTeamDataRequired && (isInFlight || isInFlight === undefined);

    const ErrorComponent = (status: number, errorMessage: string) => (
      <div
        data-qa={`error-component-${status}`}
        className="jg-space-ptmd col-md-8 offset-md-2"
      >
        <ErrorPage status={status} errorMessage={errorMessage} />
      </div>
    );

    switch (true) {
      case errorStatus === '404':
        return ErrorComponent(404, t('content.error.api.teamnotfound'));
      case errorStatus === '400':
        return ErrorComponent(400, t('content.error.api.badrequest'));
      case errorStatus === '403':
        return ErrorComponent(403, t('content.error.api.forbidden'));
      case isFatalError:
        return ErrorComponent(500, t('content.error.api.generic'));
      case noPageId:
        return (
          <Alert
            type="error"
            displayType="inline"
            className="jg-text--pull-left"
            data-qa="no-frpid"
          >
            <Trans i18nKey="content.error.api.generic.nofrp">
              Oops! Fundraising page not found.
              <a href={config.createFRPUri}>Create a fundraising page</a> to
              start a team
            </Trans>
          </Alert>
        );
      case displayLoading:
        return (
          <Loading size="medium" colour="purple" className="jg-space-ptlg" />
        );
      default:
        return (
          <ErrorBoundary
            fallbackContent={ErrorComponent}
            onError={this.onComponentDidCatch}
          >
            <TrackingProvider
              analytics={analytics}
              defaultTrackingData={{
                page_type: 'team page',
                content_type: 'team_page_id',
                charity_id: charityId,
                page_id: pageId,
                content_id: teamGuid,
              }}
            >
              <Switch>
                <PageOwnerRoute path={ROUTES.EDIT_TEAM}>
                  <AdminTabs active="edit" />
                </PageOwnerRoute>
                <PageOwnerRoute path={ROUTES.EDIT_MEMBERS}>
                  <AdminTabs active="members" />
                </PageOwnerRoute>
                <PageOwnerRoute path={ROUTES.SETTINGS}>
                  <AdminTabs active="settings" />
                </PageOwnerRoute>
              </Switch>
              <div className="container jg-space-pvsm@md jg-space-phlg@md jg-bd jg-br jg-bd--very-light-grey jg-background--white">
                <Switch>
                  <PageOwnerRoute
                    path={ROUTES.EDIT_TEAM}
                    component={EditTeam}
                  />
                  <PageOwnerRoute path={ROUTES.SETTINGS} component={Settings} />
                  <PageOwnerRoute
                    path={ROUTES.EDIT_MEMBERS}
                    component={EditMembers}
                  />
                </Switch>
              </div>
            </TrackingProvider>
          </ErrorBoundary>
        );
    }
  }
}

export const mapDispatchToProps: IAppComponentDispatchToProps = {
  savePageId: appActions.savePageId,
  savePageType: appActions.savePageType,
  fetchAppData: apiThunk.fetchAppData,
};

export const mapStateToProps = (
  state: IAppState,
): IAppComponentMapStateToProps => ({
  isInFlight: getRequestIsInFlight(state, 'GET_APP_DATA'),
  isFatalError: Boolean(getRequestIsError(state, 'GET_APP_DATA')),
  charityId: getTeamCharityId(state),
  pageId: getPageId(state),
  teamGuid: getTeamGuid(state),
  errorStatus: state.api.errorStatus,
});

export default withTranslation()(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(App)),
);
